import { useTheme } from '@/context/ThemeContext';

export default function Process() {
  const { isDark } = useTheme();

  const steps = [
    { number: '01', title: 'Operational Audit', description: 'We evaluate workflows, bottlenecks, and operational inefficiencies.', icon: 'ri-search-line' },
    { number: '02', title: 'System Architecture', description: 'We design the automation and AI infrastructure required to coordinate operations.', icon: 'ri-layout-grid-line' },
    { number: '03', title: 'Deployment', description: 'Our team builds and integrates the systems across your business tools.', icon: 'ri-rocket-line' },
    { number: '04', title: 'Optimization', description: 'We monitor and refine the system to ensure continued performance and scalability.', icon: 'ri-line-chart-line' },
  ];

  return (
    <section
      className={`py-32 px-6 relative overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#111D2C]' : 'bg-[#F5F8FA]'}`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="text-[#14B8A6] text-xs font-semibold tracking-[0.3em] uppercase mb-6">OUR PROCESS</div>
          <h2 className={`text-5xl md:text-6xl font-bold mb-6 tracking-tight transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0D1B2A]'}`}>
            How We Build Intelligent Systems
          </h2>
          <p className={`text-lg max-w-2xl mx-auto leading-relaxed transition-colors duration-500 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            A structured approach to transforming operational complexity into coordinated intelligence.
          </p>
        </div>

        <div className="relative">
          {/* Connector Line */}
          <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-[#14B8A6]/20 via-[#14B8A6]/50 to-[#14B8A6]/20 hidden lg:block">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#14B8A6] to-transparent animate-pulse" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <div
                  className={`relative backdrop-blur-xl border rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${
                    isDark
                      ? 'bg-[#1A2332]/50 border-[#14B8A6]/10 hover:border-[#14B8A6]/30'
                      : 'bg-white border-gray-100 hover:border-[#14B8A6]/30 hover:shadow-xl'
                  }`}
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-[#14B8A6]/10 rounded-2xl mb-6 group-hover:bg-[#14B8A6]/20 group-hover:scale-110 transition-all duration-500">
                    <i className={`${step.icon} text-3xl text-[#14B8A6]`} />
                  </div>
                  <div className={`text-7xl font-bold mb-4 tracking-tighter leading-none transition-colors duration-500 ${isDark ? 'text-[#14B8A6]/20' : 'text-[#14B8A6]/25'}`}>
                    {step.number}
                  </div>
                  <h3 className={`text-2xl font-bold mb-4 tracking-tight transition-colors duration-500 ${isDark ? 'text-white' : 'text-[#0D1B2A]'}`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm leading-relaxed transition-colors duration-500 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {step.description}
                  </p>
                  <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#14B8A6]/5 to-transparent rounded-3xl" />
                  </div>
                </div>

                <div
                  className={`absolute top-20 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#14B8A6] rounded-full z-10 hidden lg:block group-hover:scale-150 transition-transform duration-500 border-4 ${
                    isDark ? 'border-[#111D2C]' : 'border-[#F5F8FA]'
                  }`}
                >
                  <div className="absolute inset-0 bg-[#14B8A6] rounded-full animate-ping opacity-75" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
